import { useState, useEffect, useRef } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import Joyride, { CallBackProps, Step } from "react-joyride";

// import FindId from "./FindId"; // Commented out for now
import FindPassword from "./FindPassword";
import ActivateAccount from "./ActivateAccount";
import { useTheme } from "@/comn/hooks";
import { _TEST1234 } from "@/comn/features/config/ApiConfig";

const FindAccount = () => {
    const { t } = useTranslation();
    const { theme } = useTheme();

    // Changed default active tab state to "password" since ID is hidden
    const [activeTab, setActiveTab] = useState<"id" | "password" | "activate">("password");

    /* ---------------- Guided tour state ---------------- */
    const [runTour, setRunTour] = useState(false);
    const [isMounted, setIsMounted] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const tourStartedRef = useRef(false);

    useEffect(() => {
        localStorage.removeItem("findAccountTour");
        setIsMounted(true);

        if (tourStartedRef.current) return;

        const hasSeenTour = localStorage.getItem("findAccountTour");

        if (!hasSeenTour) {
            const startTour = () => {
                if (!tourStartedRef.current) {
                    tourStartedRef.current = true;
                    setRunTour(true);
                }
            };
            setTimeout(startTour, 300);
        }
    }, []);

    /* ---------------- Determine system type ---------------- */
    const isInternal = _TEST1234.SYSTEM === "internal"; // tancis
    const isExternal = _TEST1234.SYSTEM === "external"; // tanesw / tanoga

    /* ---------------- Joyride steps ---------------- */
    const steps: Step[] = [
        {
            target: ".find-header",
            content: (
                <>
                    This page helps you recover your account credentials if you have forgotten your
                    password, or if you need to activate your account.
                </>
            ),
            disableBeacon: true,
            placement: "bottom",
        },
        /* Commented out Find ID Joyride Steps
        {
            target: ".tab-id-btn",
            content: (
                <>
                    Select this tab to retrieve your <strong>User ID</strong>. The required
                    information is explained in the next step.
                </>
            ),
            disableBeacon: true,
            placement: "bottom",
        },
        {
            target: ".find-body",
            content: (
                <div className="flex flex-col gap-4">
                    {isInternal && (
                        <div className="p-4 bg-blue-50 border-l-4 border-blue-500 rounded-md shadow-sm">
                            <strong>INTERNAL USERS</strong>
                            <p className="mt-1 text-sm">
                                In Name enter your <strong>FULL NAME</strong> (e.g. JOHN JACK DOE)
                                and in Employee No enter your{" "}
                                <strong>EMPLOYEE NUMBER</strong> (e.g. 0XXXX).
                            </p>
                        </div>
                    )}
                    {isExternal && (
                        <div className="p-4 bg-green-50 border-l-4 border-green-500 rounded-md shadow-sm">
                            <strong>EXTERNAL USERS</strong>
                            <p className="mt-1 text-sm">
                                Enter your <strong>FULL NAME</strong> and the{" "}
                                <strong>VALID EMAIL</strong> used during account registration (e.g.
                                juma@gmail.com).
                            </p>
                        </div>
                    )}
                </div>
            ),
            disableBeacon: true,
            placement: "top",
        },
        */
        {
            target: ".tab-password-btn",
            content: (
                <>
                    Select this tab to reset your password if you remember your User ID but are
                    unable to access your account.
                </>
            ),
            disableBeacon: true,
            placement: "bottom",
        },
        {
            target: ".find-body",
            content: (
                <div className="flex flex-col gap-4">
                    {isInternal && (
                        <div className="p-4 bg-blue-50 border-l-4 border-blue-500 rounded-md shadow-sm">
                            <strong>INTERNAL USERS</strong>
                            <p className="mt-1 text-sm">
                                In ID enter your <strong>DOMAIN ID</strong> (e.g. JOHN.DOE) and in
                                Email enter your official work <strong>EMAIL</strong> address (e.g.
                                john.doe@tra.go.tz).
                            </p>
                        </div>
                    )}
                    {isExternal && (
                        <div className="p-4 bg-green-50 border-l-4 border-green-500 rounded-md shadow-sm">
                            <strong>EXTERNAL USERS</strong>
                            <p className="mt-1 text-sm">
                                In ID enter your registered <strong>TIN NUMBER</strong> (e.g.
                                123123123) and the <strong>VALID EMAIL</strong> (e.g.
                                juma@gmail.com) provided during account registration.
                            </p>
                        </div>
                    )}
                </div>
            ),
            disableBeacon: true,
            placement: "top",
        },
        // ---- Activate Account steps ----
        {
            target: ".tab-activate-btn",
            content: (
                <>
                    Select this tab to <strong>Activate your account</strong>. If your account has
                    been locked, you can self-activate it here to restore access without contacting
                    an administrator.
                </>
            ),
            disableBeacon: true,
            placement: "bottom",
        },
        {
            target: ".find-body",
            content: (
                <div className="flex flex-col gap-4">
                    <p className="text-sm font-semibold">Step 1 — Enter your details</p>
                    {isInternal && (
                        <div className="p-4 bg-blue-50 border-l-4 border-blue-500 rounded-md shadow-sm">
                            <strong>INTERNAL USERS</strong>
                            <p className="mt-1 text-sm">
                                Enter your <strong>DOMAIN ID</strong> (e.g. JOHN.DOE) and your
                                official work <strong>EMAIL</strong> (e.g. john.doe@tra.go.tz).
                            </p>
                        </div>
                    )}
                    {isExternal && (
                        <div className="p-4 bg-green-50 border-l-4 border-green-500 rounded-md shadow-sm">
                            <strong>EXTERNAL USERS</strong>
                            <p className="mt-1 text-sm">
                                Enter your registered <strong>USER ID</strong> and the{" "}
                                <strong>VALID EMAIL</strong> (e.g. juma@gmail.com) used during
                                account registration.
                            </p>
                        </div>
                    )}
                </div>
            ),
            disableBeacon: true,
            placement: "top",
        },
        {
            target: ".find-body",
            content: (
                <div className="flex flex-col gap-4">
                    <p className="text-sm font-semibold">Step 2 — Verify via OTP</p>
                    <div className="p-4 bg-yellow-50 border-l-4 border-yellow-500 rounded-md shadow-sm">
                        <p className="text-sm">
                            After clicking <strong>Activate Account</strong>, a{" "}
                            <strong>8-digit OTP</strong> will be sent to your registered email
                            address. Open the OTP dialog, enter the code, and click{" "}
                            <strong>Verify</strong>.
                        </p>
                        <p className="mt-2 text-xs text-gray-500">
                            The code expires in <strong>2 minutes</strong>. If it expires, use the{" "}
                            <strong>Resend OTP</strong> link to get a new one. You have{" "}
                            <strong>3 attempts</strong> before the input is locked.
                        </p>
                    </div>
                </div>
            ),
            disableBeacon: true,
            placement: "top",
        },
    ];

    const validSteps = isMounted
        ? steps.filter((step) => {
            if (
                step.target === ".find-header" ||
                step.target === ".tab-id-btn" ||
                step.target === ".tab-password-btn" ||
                step.target === ".tab-activate-btn"
            ) {
                return document.querySelector(step.target as string);
            }
            return true;
        })
        : [];

    /* ---------------- Joyride callback ---------------- */
    const handleJoyrideCallback = (data: CallBackProps) => {
        const { index, status } = data;

        setCurrentStep(index);

        // Adjusted indices to map dynamically now that step length changed
        if (index === 1) setActiveTab("password");
        if (index === 2) setActiveTab("password");
        if (index === 3) setActiveTab("activate");
        if (index === 4) setActiveTab("activate");
        if (index === 5) setActiveTab("activate");

        if (status === "finished" || status === "skipped") {
            setRunTour(false);
            localStorage.setItem("findAccountTour", "true");
        }
    };

    return (
        <div className="w-full h-auto lg:h-[calc(100vh-250px)] flex items-center relative">
            {/* ---------------- Joyride ---------------- */}
            {isMounted && validSteps.length > 0 && (
                <Joyride
                    steps={validSteps}
                    run={runTour}
                    continuous={true}
                    showSkipButton={true}
                    showProgress={true}
                    scrollToFirstStep={true}
                    disableOverlayClose={false}
                    spotlightClicks={false}
                    hideBackButton={false}
                    styles={{
                        options: {
                            zIndex: 10000,
                            primaryColor: "#007bff",
                            width: 450,
                            arrowColor: "#fff",
                        },
                        tooltipContainer: {
                            padding: "20px",
                            borderRadius: "12px",
                        },
                    }}
                    locale={{
                        back: "Back",
                        close: "Close",
                        last: "Finish",
                        next: "Next",
                        skip: "Skip",
                    }}
                    callback={handleJoyrideCallback}
                />
            )}

            <div className="w-full flex items-center justify-center my-[70px]">
                <div className="w-full md:w-[520px] lg:w-[520px]">
                    {/* ---------------- Header ---------------- */}
                    <div className="find-header bg-primary p-[30px] relative rounded-t-[30px]">
                        <div className="flex items-center justify-between">
                            <h2 className="text-2xl text-white">
                                {activeTab === "id" && t("L_FIND_ID")}
                                {activeTab === "password" && t("L_FIND_PWD")}
                                {activeTab === "activate" && t("L_ACTIVATE_ACCOUNT")}
                            </h2>

                            {/* Tabs */}
                            {/* Tabs */}
                            <div className="flex bg-white/10 rounded-full p-[4px]">
                                {(["id", "password", "activate"] as const).map((tab) => {
                                    // Skip rendering the 'id' button tab entirely
                                    if (tab === "id") return null;

                                    const isActive = activeTab === tab;
                                    return (
                                        <button
                                            key={tab}
                                            type="button"
                                            className={classNames(
                                                "px-[14px] py-[6px] text-xs rounded-2xl transition ml-1",
                                                {
                                                    "bg-white text-black": isActive,
                                                    "bg-transparent text-white hover:bg-white/20": !isActive,
                                                    // Removed the "tab-id-btn" comparison completely to fix the TS2367 error
                                                    "tab-password-btn": tab === "password",
                                                    "tab-activate-btn": tab === "activate",
                                                }
                                            )}
                                            onClick={() => setActiveTab(tab)}
                                        >
                                            {tab === "password" && t("L_FIND_PWD")}
                                            {tab === "activate" && t("L_ACTIVATE_ACCOUNT")}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Decorative image placeholder */}
                        <span className="block w-[180px] lg:w-[236px] absolute -top-[84px] -right-[10px] lg:-right-[80px]">
                            {/* intentionally empty */}
                        </span>
                    </div>

                    {/* ---------------- Body ---------------- */}
                    <div className="find-body bg-card rounded-b-[30px] shadow-lg">
                        {/* {activeTab === "id" && <FindId embedded />} */}
                        {activeTab === "password" && <FindPassword embedded />}
                        {activeTab === "activate" && <ActivateAccount embedded />}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FindAccount;